import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export interface LocationState {
  accuracy: number;
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  latitude: number;
  longitude: number;
  speed: number | null;
  timestamp: number;
}

const initialLocation: LocationState = {
  accuracy: 0,
  altitude: 0,
  altitudeAccuracy: 0,
  heading: 0,
  latitude: 46.201746,
  longitude: 6.140708,
  speed: 0,
  timestamp: Date.now()
};

interface LocationContextType {
  userLocation: LocationState;
  setUserLocation: (loc: LocationState) => void;
  clearUserLocation: () => void;
}

// Si tu préfères un localStorage distinct pour la location :
const LOCATION_STORAGE_KEY = 'user_location_state';

const loadLocationState = (): LocationState => {
  try {
    const serialized = localStorage.getItem(LOCATION_STORAGE_KEY);
    return serialized !== null ? JSON.parse(serialized) : initialLocation;
  } catch (err) {
    console.error('Error loading location state:', err);
    return initialLocation;
  }
};

const saveLocationState = (loc: LocationState) => {
  try {
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(loc));
  } catch (err) {
    console.error('Error saving location state:', err);
  }
};

const LocationContext = createContext<LocationContextType | null>(null);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location, setLocationState] = useState<LocationState>(() => loadLocationState());

  useEffect(() => {
    saveLocationState(location);
  }, [location]);

  const setLocation = useCallback((loc: LocationState) => {
    setLocationState(loc);
  }, []);

  const clearLocation = useCallback(() => {
    setLocationState(initialLocation);
  }, []);

  const value: LocationContextType = {
    userLocation: location,
    setUserLocation: setLocation,
    clearUserLocation: clearLocation
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = () => {
  const ctx = useContext(LocationContext);
  if (ctx == null) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return ctx;
};
